// Upload Session Management for Resumable Uploads
// Handles persistence, recovery, and synchronization of chunked upload sessions

import { loggerUtils } from './logger';

export interface UploadSession {
  // Session identification
  sessionId: string;
  uploadId: string;
  
  // File metadata
  filename: string;
  bucketId: number;
  totalSize: number;
  mimeType?: string;
  
  // Upload progress
  uploadedBytes: number;
  chunkSize: number;
  lastChunkOffset: number;
  
  // Session state
  status: 'pending' | 'uploading' | 'paused' | 'completed' | 'error' | 'cancelled';
  error?: string;
  
  // Timing
  createdAt: number;
  lastActiveAt: number;
  resumedAt?: number;
  pausedAt?: number;
  
  // Server sync
  expiresAt: string;
  serverSyncedAt?: number;
  
  // Retry information
  retryCount: number;
  maxRetries: number;
}

export interface UploadSessionOptions {
  maxRetries?: number;
  syncIntervalMs?: number;
  maxSessionAgeMs?: number;
  cleanupIntervalMs?: number;
}

class UploadSessionManager {
  private sessions = new Map<string, UploadSession>();
  private syncTimers = new Map<string, NodeJS.Timeout>();
  private cleanupTimer?: NodeJS.Timeout;
  
  private readonly storageKey = 'stash_upload_sessions';
  private readonly options: Required<UploadSessionOptions>;

  constructor(options: UploadSessionOptions = {}) {
    this.options = {
      maxRetries: 3,
      syncIntervalMs: 30000, // 30 seconds
      maxSessionAgeMs: 24 * 60 * 60 * 1000, // 24 hours
      cleanupIntervalMs: 5 * 60 * 1000, // 5 minutes
      ...options
    };
    
    this.loadSessions();
    this.startCleanupTimer();
  }

  // Create a new upload session
  createSession(
    uploadId: string,
    filename: string,
    bucketId: number,
    totalSize: number,
    chunkSize: number,
    expiresAt: string,
    mimeType?: string
  ): UploadSession {
    const sessionId = this.generateSessionId();
    const now = Date.now();
    
    const session: UploadSession = {
      sessionId,
      uploadId,
      filename,
      bucketId,
      totalSize,
      mimeType,
      uploadedBytes: 0,
      chunkSize,
      lastChunkOffset: 0,
      status: 'pending',
      createdAt: now,
      lastActiveAt: now,
      expiresAt,
      retryCount: 0,
      maxRetries: this.options.maxRetries
    };
    
    this.sessions.set(sessionId, session);
    this.persistSessions();
    this.startSyncTimer(sessionId);
    
    return session;
  }

  // Get session by ID
  getSession(sessionId: string): UploadSession | undefined {
    return this.sessions.get(sessionId);
  }

  // Get session by upload ID
  getSessionByUploadId(uploadId: string): UploadSession | undefined {
    for (const session of this.sessions.values()) {
      if (session.uploadId === uploadId) {
        return session;
      }
    }
    return undefined;
  }

  // Get all active sessions
  getActiveSessions(): UploadSession[] {
    return Array.from(this.sessions.values()).filter(
      session => session.status !== 'completed' && session.status !== 'cancelled'
    );
  }

  // Update session progress
  updateProgress(sessionId: string, uploadedBytes: number, lastChunkOffset: number): void {
    const session = this.sessions.get(sessionId);
    if (!session) return;
    
    session.uploadedBytes = uploadedBytes;
    session.lastChunkOffset = lastChunkOffset;
    session.lastActiveAt = Date.now();
    
    // Mark as uploading if not already
    if (session.status === 'pending' || session.status === 'paused') {
      session.status = 'uploading';
      session.resumedAt = Date.now();
    }
    
    this.persistSessions();
  }

  // Update session status
  updateStatus(sessionId: string, status: UploadSession['status'], error?: string): void {
    const session = this.sessions.get(sessionId);
    if (!session) return;
    
    const now = Date.now();
    session.status = status;
    session.lastActiveAt = now;
    
    if (error) {
      session.error = error;
      session.retryCount++;
    }
    
    if (status === 'paused') {
      session.pausedAt = now;
    } else if (status === 'uploading' && session.status !== 'uploading') {
      session.resumedAt = now;
    }
    
    this.persistSessions();
    
    // Stop sync timer for completed/cancelled sessions
    if (status === 'completed' || status === 'cancelled') {
      this.stopSyncTimer(sessionId);
    }
  }

  // Pause a session
  pauseSession(sessionId: string): boolean {
    const session = this.sessions.get(sessionId);
    if (!session || session.status !== 'uploading') {
      return false;
    }
    
    this.updateStatus(sessionId, 'paused');
    this.stopSyncTimer(sessionId);
    return true;
  }

  // Resume a session
  resumeSession(sessionId: string): boolean {
    const session = this.sessions.get(sessionId);
    if (!session || session.status !== 'paused') {
      return false;
    }
    
    // Check if session hasn't expired
    if (new Date(session.expiresAt).getTime() < Date.now()) {
      this.updateStatus(sessionId, 'error', 'Upload session expired');
      return false;
    }
    
    this.updateStatus(sessionId, 'pending');
    this.startSyncTimer(sessionId);
    return true;
  }

  // Cancel and remove a session
  cancelSession(sessionId: string): boolean {
    const session = this.sessions.get(sessionId);
    if (!session) return false;
    
    this.updateStatus(sessionId, 'cancelled');
    this.stopSyncTimer(sessionId);
    
    // Remove from storage after a brief delay to allow for cleanup
    setTimeout(() => {
      this.sessions.delete(sessionId);
      this.persistSessions();
    }, 1000);
    
    return true;
  }

  // Complete a session
  completeSession(sessionId: string, _fileId?: number): boolean {
    const session = this.sessions.get(sessionId);
    if (!session) return false;
    
    this.updateStatus(sessionId, 'completed');
    
    // Remove completed sessions after a delay
    setTimeout(() => {
      this.sessions.delete(sessionId);
      this.persistSessions();
    }, 5000);
    
    return true;
  }

  // Sync session with server status
  async syncWithServer(sessionId: string, getUploadStatus: (uploadId: string) => Promise<Record<string, unknown>>): Promise<void> {
    const session = this.sessions.get(sessionId);
    if (!session || session.status === 'completed' || session.status === 'cancelled') {
      return;
    }
    
    try {
      const serverStatus = await getUploadStatus(session.uploadId);
      
      // Update local session with server state
      if ((serverStatus.uploaded_bytes as number) !== session.uploadedBytes) {
        session.uploadedBytes = serverStatus.uploaded_bytes as number;
        session.lastChunkOffset = serverStatus.uploaded_bytes as number;
        this.persistSessions();
      }
      
      // Check if completed on server
      if (serverStatus.completed && session.status !== 'completed') {
        this.completeSession(sessionId, serverStatus.file_id as string);
      }
      
      session.serverSyncedAt = Date.now();
      
    } catch (error) {
      loggerUtils.error(error instanceof Error ? error : new Error(String(error)), 'session_sync', { sessionId });
    }
  }

  // Check if session can be retried
  canRetry(sessionId: string): boolean {
    const session = this.sessions.get(sessionId);
    if (!session) return false;
    
    return session.status === 'error' && 
           session.retryCount < session.maxRetries &&
           new Date(session.expiresAt).getTime() > Date.now();
  }

  // Private methods

  private generateSessionId(): string {
    return `upload_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  private loadSessions(): void {
    // Skip localStorage access during SSR
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return;
    }

    try {
      const stored = localStorage.getItem(this.storageKey);
      if (!stored) return;
      
      const sessionData = JSON.parse(stored);
      
      // Restore sessions, filtering out expired ones
      const now = Date.now();
      for (const [sessionId, session] of Object.entries(sessionData)) {
        const uploadSession = session as UploadSession;
        
        // Skip expired or very old sessions
        if (new Date(uploadSession.expiresAt).getTime() < now ||
            now - uploadSession.createdAt > this.options.maxSessionAgeMs) {
          continue;
        }
        
        // Reset uploading status to paused for recovery
        if (uploadSession.status === 'uploading') {
          uploadSession.status = 'paused';
        }
        
        this.sessions.set(sessionId, uploadSession);
        
        // Start sync timer for active sessions
        if (uploadSession.status !== 'completed' && uploadSession.status !== 'cancelled') {
          this.startSyncTimer(sessionId);
        }
      }
      
    } catch (error) {
      loggerUtils.error(error instanceof Error ? error : new Error(String(error)), 'session_load');
    }
  }

  private persistSessions(): void {
    // Skip localStorage access during SSR
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return;
    }

    try {
      const sessionData = Object.fromEntries(this.sessions.entries());
      localStorage.setItem(this.storageKey, JSON.stringify(sessionData));
    } catch (error) {
      loggerUtils.error(error instanceof Error ? error : new Error(String(error)), 'session_persist');
    }
  }

  private startSyncTimer(sessionId: string): void {
    this.stopSyncTimer(sessionId);
    
    const timer = setInterval(async () => {
      const session = this.sessions.get(sessionId);
      if (!session || session.status === 'completed' || session.status === 'cancelled') {
        this.stopSyncTimer(sessionId);
        return;
      }
      
      // Only sync active uploading sessions
      if (session.status === 'uploading' && this.getUploadStatusCallback) {
        try {
          await this.syncWithServer(sessionId, this.getUploadStatusCallback);
        } catch (error) {
          loggerUtils.error(error instanceof Error ? error : new Error(String(error)), 'session_periodic_sync', { sessionId });
        }
      }
    }, this.options.syncIntervalMs);
    
    this.syncTimers.set(sessionId, timer);
  }

  // Callback for getting upload status from server
  private getUploadStatusCallback?: (uploadId: string) => Promise<Record<string, unknown>>;

  setUploadStatusCallback(callback: (uploadId: string) => Promise<Record<string, unknown>>): void {
    this.getUploadStatusCallback = callback;
  }

  // File validation for recovery
  validateFileForRecovery(session: UploadSession, file: File): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    // Check filename match
    if (session.filename !== file.name) {
      errors.push(`Filename mismatch: expected "${session.filename}", got "${file.name}"`);
    }

    // Check file size match
    if (session.totalSize !== file.size) {
      errors.push(`File size mismatch: expected ${this.formatFileSize(session.totalSize)}, got ${this.formatFileSize(file.size)}`);
    }

    // Check mime type match (if available in session)
    if (session.mimeType && session.mimeType !== file.type) {
      // Be lenient with mime type as it can vary across systems
      const sessionType = session.mimeType.split('/')[0]; // e.g., "image" from "image/jpeg"
      const fileType = file.type.split('/')[0];
      
      if (sessionType !== fileType) {
        errors.push(`File type mismatch: expected ${session.mimeType}, got ${file.type}`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Associate file with existing session for recovery
  associateFileWithSession(sessionId: string, file: File): boolean {
    const session = this.sessions.get(sessionId);
    if (!session) return false;

    const validation = this.validateFileForRecovery(session, file);
    if (!validation.isValid) {
      loggerUtils.error(new Error(`File validation failed: ${validation.errors.join(', ')}`), 'session_file_validation', { sessionId });
      return false;
    }

    // Update session with any missing metadata
    if (!session.mimeType && file.type) {
      session.mimeType = file.type;
      this.persistSessions();
    }

    return true;
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  }

  private stopSyncTimer(sessionId: string): void {
    const timer = this.syncTimers.get(sessionId);
    if (timer) {
      clearInterval(timer);
      this.syncTimers.delete(sessionId);
    }
  }

  private startCleanupTimer(): void {
    this.cleanupTimer = setInterval(() => {
      this.cleanup();
    }, this.options.cleanupIntervalMs);
  }

  private cleanup(): void {
    const now = Date.now();
    const sessionsToRemove: string[] = [];
    
    for (const [sessionId, session] of this.sessions.entries()) {
      // Remove expired sessions
      if (new Date(session.expiresAt).getTime() < now) {
        sessionsToRemove.push(sessionId);
        continue;
      }
      
      // Remove very old sessions
      if (now - session.createdAt > this.options.maxSessionAgeMs) {
        sessionsToRemove.push(sessionId);
        continue;
      }
      
      // Remove completed sessions older than 1 hour
      if (session.status === 'completed' && 
          now - session.lastActiveAt > 60 * 60 * 1000) {
        sessionsToRemove.push(sessionId);
        continue;
      }
    }
    
    // Remove identified sessions
    for (const sessionId of sessionsToRemove) {
      this.sessions.delete(sessionId);
      this.stopSyncTimer(sessionId);
    }
    
    if (sessionsToRemove.length > 0) {
      this.persistSessions();
    }
  }

  // Cleanup on page unload
  destroy(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }
    
    for (const timer of this.syncTimers.values()) {
      clearInterval(timer);
    }
    
    this.syncTimers.clear();
    this.persistSessions();
  }
}

export const uploadSessionManager = new UploadSessionManager();

// Clean up on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    uploadSessionManager.destroy();
  });
}

// Export the interface and class for use in other modules  
export type { UploadSession, UploadSessionOptions };
export { UploadSessionManager };